import { Typography } from "antd";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import { SwitchingBetweenApps } from "@tailormed/common-client/component/SwitchingBetweenApps";
import {
  CoreSideNav,
  DisabledMenuItemText,
  LogoButton,
  ModuleSelector,
  ModuleSelectorLeft,
  ProductIcon,
  ProductLabel
} from "@tailormed/common-client/component/AppShell";
import { TmLogoLockup } from "@tailormed/common-client/assets/svg/TmLogoLockup";
import { TmArchesMark } from "@tailormed/common-client/assets/svg/TmArchesMark";

import { useGeneralMenusItems, MenuItemKeys } from "./MenuItems";
import { history } from "../../router/history";
import {
  selectBadgeCounts,
  selectFiltersModified,
  selectFormPopups,
  selectSelectedView,
  selectShowSupport,
  selectFilterListStatuses,
  selectUser,
  selectIsBulkActionDirty
} from "../../store/selector";
import { Routes } from "../../constant/routes";
import { PatientConfirmationAlertContext } from "../../context/PatientConfirmationAlertContext";
import ACTIONS from "../../store/action";
import { defaultFilters } from "../../constant/filters";
import { useGetSetting } from "../../hooks/getSetting";
import { ContactSupport } from "../../component/ContactSupport";
import AddNewPatientButton from "../addNewPatient/AddNewPatientButton";
import BulkActionDiscardPopup from "../BulkActions/BulkActionDiscardPopup";
import { setPendingBulkLeaveTeardown } from "../BulkActions/pendingBulkLeaveTeardown";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import { selectedViews } from "../../constant/table";
import { ReactComponent as ArrowDownIcon } from "../../assets/svg/arrow-down-icon.svg";
import { SIDE_BAR_COLLAPSED, getStoredSidebarCollapsed } from "../../utils/sidebarCollapsed";

const { Text } = Typography;
const ALL_ENROLLMENTS = "all-enrollments";

const SidebarMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();

  const selectedView = useSelector(selectSelectedView);
  const filtersModified = useSelector(selectFiltersModified);
  const filterListStatuses = useSelector(selectFilterListStatuses);
  const isBulkActionDirty = useSelector(selectIsBulkActionDirty);
  const badgeCounts = useSelector(selectBadgeCounts);
  const formPopups = useSelector(selectFormPopups);
  const showContactSupport = useSelector(selectShowSupport);
  const user = useSelector(selectUser);

  const isContactSupportVisible = showContactSupport.visible;

  const { showCancelConfirm } = useContext(PatientConfirmationAlertContext);

  const [collapsed, setCollapsed] = useState(getStoredSidebarCollapsed);
  const [selectedSubMenuKey, setSelectedSubMenuKey] = useState([]);

  const updateFilterListStatusesAct = (filterListID, canModify, showDiscardModal, nextSelectedView) =>
    dispatch(ACTIONS.updateFilterListStatuses(filterListID, canModify, showDiscardModal, nextSelectedView));
  const updateIsSidebarCollapsedAct = (value) => dispatch(ACTIONS.setIsSidebarCollapsed(value));

  const { menuItems: sideBarMenus, bulkNavGuard } = useGeneralMenusItems(badgeCounts, selectedView, filtersModified);

  // Settings + Help render as an icon row pinned to the sidebar bottom.
  const settingsMenu = sideBarMenus.find((menu) => menu.key === MenuItemKeys.settings.key);
  const helpMenu = sideBarMenus.find((menu) => menu.key === MenuItemKeys.help_center.key);
  const mainMenus = sideBarMenus.filter(
    (menu) => menu.key !== MenuItemKeys.settings.key && menu.key !== MenuItemKeys.help_center.key
  );

  const updateSelectedSubMenuKey = (prevKeys, selectedKey) => {
    let keyToKeep = null;

    if (isContactSupportVisible) {
      keyToKeep = MenuItemKeys.help_center.subItems.contact_support;
    }

    if (formPopups.visible) {
      keyToKeep =
        formPopups.contentKey === formPopupsContentKeys.NOTIFICATIONS
          ? MenuItemKeys.home.subItems.notifications
          : formPopups.contentKey === formPopupsContentKeys.TASKS
          ? MenuItemKeys.home.subItems.tasks
          : null;
    }
    const updatedKeys = selectedKey ? [keyToKeep, selectedKey].filter(Boolean) : [keyToKeep].filter(Boolean);
    return updatedKeys.length ? updatedKeys : !isContactSupportVisible ? [] : prevKeys;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const selectedKey = queryParams.get("selectedView");

    setSelectedSubMenuKey((prevKeys) => {
      return updateSelectedSubMenuKey(prevKeys, selectedKey);
    });
  }, [search, formPopups, isContactSupportVisible]);

  useEffect(() => {
    localStorage.setItem(SIDE_BAR_COLLAPSED, JSON.stringify(collapsed));
    updateIsSidebarCollapsedAct(collapsed);
  }, [collapsed]);

  const handleDashboardClick = useCallback(() => {
    if (pathname.includes(Routes.NEW_PATIENT)) {
      return showCancelConfirm(Routes.ROOT);
    }

    // The dashboard teardown for the logo click. Only ever run when the navigation
    // is actually going to proceed — never before a guard has decided — so a "Stay"
    // leaves the current view (e.g. an open saved list) completely intact.
    const runDashboardTeardown = () => {
      dispatch(ACTIONS.clearSorterAction(selectedView));
      filtersModified && dispatch(ACTIONS.setFiltersAction(defaultFilters));
      dispatch(ACTIONS.clearSelectedView());
      dispatch(ACTIONS.clearFiltersInputs());
      // Reset the open saved list's data (filterList → {}) so leaving a saved list via
      // the logo doesn't leave stale state that would pre-populate the next
      // "Save as New List" form. No-op (empty filterList) when not on a saved list.
      dispatch(ACTIONS.clearSavedListRelatedDataAction());
      dispatch(ACTIONS.markSavedFilterAsChanged(undefined, false));
    };

    // Bulk-dirty FIRST — the bulk guard owns the leave so its "Discard bulk action?" popup
    // is never skipped, even on a saved list with unsaved filter edits (canModify). On a
    // canModify list the global guard chains from its Discard into the filter modal; on a
    // bulk-only list the registered teardown runs on its Discard & Leave. This branch must
    // come before the pure-canModify filter-modal branch below.
    if (isBulkActionDirty) {
      if (pathname.includes(Routes.FILTER_LIST)) {
        // Leaving a saved list → "/" is a PATHNAME change, owned by the global
        // BulkActionNavGuard. Register the teardown for its terminal "Discard & Leave"
        // (bulk-only case) and attempt the push; the guard blocks it and shows its popup.
        // "Stay" runs nothing. (Double-dirty: the guard drops this baton and chains to the
        // filter modal, which owns the terminal teardown instead.)
        setPendingBulkLeaveTeardown(runDashboardTeardown);
        history.push(Routes.ROOT);
        return;
      }
      // From a bucket view, logo → "/" is a SAME-pathname switch, which the global guard
      // ignores; MenuItems' in-sidebar guard owns it (defers behind its popup, runs the
      // teardown + push on confirm, nothing on "Stay").
      bulkNavGuard.guardBulkNavAction(() => {
        runDashboardTeardown();
        history.push(Routes.ROOT);
      });
      return;
    }

    // Pure canModify (unsaved filter edits, no dirty bulk action): show the saved-list
    // discard modal directly, as before.
    if (pathname.includes(Routes.FILTER_LIST) && filterListStatuses?.canModify) {
      updateFilterListStatusesAct(filterListStatuses?.id, filterListStatuses.canModify, true, Routes.ROOT);
      // Surfacing the discard modal instead of navigating; drop focus from the clicked logo
      // so it doesn't retain a misleading highlight/focus style if the user cancels.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      return;
    }

    // Neither dirty: navigate immediately, tearing down now.
    runDashboardTeardown();
    history.push(Routes.ROOT);
  }, [pathname, filtersModified, filterListStatuses, selectedView, isBulkActionDirty, bulkNavGuard]);

  // Module selector (product icon + label) wrapped in the shared SwitchingBetweenApps.
  const moduleSlot = (
    <SwitchingBetweenApps
      user={user}
      goToHomePage={handleDashboardClick}
      useGetSetting={useGetSetting}
      pathname={pathname}
    >
      {({ hasModulesToSwitch }) => (
        <LogoButton
          type="text"
          id="financial_navigation"
          collapsed={collapsed}
          hasModulesToSwitch={hasModulesToSwitch}
          onClick={() => {
            if (!hasModulesToSwitch) {
              handleDashboardClick();
            }
          }}
        >
          <ModuleSelector collapsed={collapsed}>
            <ModuleSelectorLeft>
              <ProductIcon module="financial-navigation" size={24} />
              {!collapsed && <ProductLabel>{t("financial_navigation")}</ProductLabel>}
            </ModuleSelectorLeft>
            {hasModulesToSwitch && <ArrowDownIcon />}
          </ModuleSelector>
        </LogoButton>
      )}
    </SwitchingBetweenApps>
  );

  // Map FNC's menu data model into the shared shell's config shape.
  const isAiGroup = (menu) => menu.key === MenuItemKeys.ai_chat_bot.key;
  const navGroups = mainMenus.map((menu) => {
    const displayed = menu.subMenus?.filter((subMenu) => subMenu.display) ?? [];
    return {
      key: menu.key,
      id: menu.key,
      pendo: `sidebar_menu.${menu.key}`,
      icon: menu.icon,
      variant: isAiGroup(menu) ? "ai" : undefined,
      collapsedLabel: isAiGroup(menu) ? t("sidebar_menu.ai_chat_bot") : menu.title,
      selected: displayed.some((subMenu) => selectedSubMenuKey.includes(subMenu.key)),
      onClick: menu.onClick,
      label: isAiGroup(menu) ? (
        // TeamMate AI title is a full-width flex row (label + right-aligned BETA tag).
        <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, marginLeft: 8 }}>{menu.title}</div>
      ) : menu.target ? (
        <Link to={menu.target} onClick={bulkNavGuard.makeLinkNavBlocker(menu.targetView)}>
          <Text>{menu.title}</Text>
        </Link>
      ) : (
        <Text>{menu.title}</Text>
      ),
      subItems: displayed.map((subMenu) => ({
        key: subMenu.key,
        id: `${subMenu.key === selectedViews.allApplications ? ALL_ENROLLMENTS : subMenu.key}_sub_menu`,
        pendo: `sidebar_menu.${subMenu.key}`,
        disabled: subMenu.disabled,
        onClick: subMenu.onClick,
        label: subMenu.disabled ? (
          <DisabledMenuItemText>{subMenu.title}</DisabledMenuItemText>
        ) : subMenu.target ? (
          <Link to={subMenu.target} onClick={bulkNavGuard.makeLinkNavBlocker(subMenu.key)}>
            <Text>{subMenu.title}</Text>
          </Link>
        ) : (
          <Text>{subMenu.title}</Text>
        )
      }))
    };
  });

  const footerItems = [settingsMenu, helpMenu].filter(Boolean).map((menu) => ({
    key: menu.key,
    id: menu.key,
    pendo: `sidebar_menu.${menu.key}`,
    icon: menu.icon,
    label: menu.title,
    selected: menu.key === MenuItemKeys.settings.key ? pathname.startsWith("/settings") : isContactSupportVisible,
    onClick: menu.onClick
  }));

  return (
    <>
      <CoreSideNav
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(!collapsed)}
        brand={{
          logoFull: <TmLogoLockup />,
          logoMark: <TmArchesMark />,
          onLogoClick: handleDashboardClick,
          logoId: "tailormed_logo"
        }}
        moduleSlot={moduleSlot}
        primaryAction={{ show: true, node: <AddNewPatientButton collapsed={collapsed} /> }}
        navGroups={navGroups}
        footerItems={footerItems}
        copyright={t("footer", { year: new Date().getFullYear() })}
        navMenuId="side_menu"
        selectedKeys={selectedSubMenuKey}
      />
      <ContactSupport />
      {bulkNavGuard.visible && (
        <BulkActionDiscardPopup
          visible={bulkNavGuard.visible}
          onClickDiscard={bulkNavGuard.onConfirm}
          onClickCancel={bulkNavGuard.onCancel}
        />
      )}
    </>
  );
};

export { SidebarMenu };
